import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Save,
  Undo2,
  Trash2,
  Layers,
  Grid3x3,
  ZoomIn,
  ZoomOut,
  Loader2,
} from "lucide-react";
import {
  PageContainer,
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const TABLE_SIZE = 90;

const DEFAULT_AREAS = ["Main Hall", "Family Area", "VIP Area", "Terrace", "Outdoor"];

const STATUS_FILL = {
  Vacant: "#D1FAE5",
  Occupied: "#FEE2E2",
  Printed: "#FEF3C7",
  Multi: "#DBEAFE",
};
const STATUS_BORDER = {
  Vacant: "#10B981",
  Occupied: "#EF4444",
  Printed: "#F59E0B",
  Multi: "#3B82F6",
};

export default function TableDesigner() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [tables, setTables] = useState([]);
  const [area, setArea] = useState("Main Hall");
  const [selected, setSelected] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const canvasRef = useRef(null);
  const dragRef = useRef({ id: null, ox: 0, oy: 0 });

  const { data: apiTables = [], isLoading } = useQuery({
    queryKey: ["tables", branchId],
    queryFn: async () => {
      const { data } = await api.get("/tables", { params: { branch_id: branchId } });
      return data.tables;
    },
    enabled: Boolean(branchId),
  });

  useEffect(() => {
    setTables(
      apiTables.map((t) => ({
        ...t,
        shape: t.shape || "square",
        w: t.w ?? TABLE_SIZE,
        h: t.h ?? TABLE_SIZE,
      })),
    );
  }, [apiTables]);

  const areas = useMemo(() => {
    const fromTables = [...new Set(tables.map((t) => t.area).filter(Boolean))];
    return fromTables.length ? fromTables : DEFAULT_AREAS;
  }, [tables]);

  useEffect(() => {
    if (!areas.length) return;
    if (!areas.includes(area)) {
      setArea(areas[0]);
    }
  }, [areas, area]);

  const visible = tables.filter((t) => t.area === area);

  const pushUndo = useCallback(() => {
    setUndoStack((prev) => [...prev.slice(-19), JSON.stringify(tables)]);
  }, [tables]);

  const onMouseDown = useCallback((e, t) => {
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = {
      id: t.id,
      ox: e.clientX - rect.left - t.x,
      oy: e.clientY - rect.top - t.y,
    };
    setSelected(t.id);
  }, []);

  const onMouseMove = useCallback((e) => {
    const { id, ox, oy } = dragRef.current;
    if (!id) return;
    const rect = canvasRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left - ox;
    let y = e.clientY - rect.top - oy;
    x = Math.round(x / 12) * 12;
    y = Math.round(y / 12) * 12;
    x = Math.max(0, Math.min(x, rect.width - 60));
    y = Math.max(0, Math.min(y, rect.height - 60));
    setTables((prev) => prev.map((t) => (t.id === id ? { ...t, x, y } : t)));
  }, []);

  const onMouseUp = () => {
    if (dragRef.current.id) {
      pushUndo();
    }
    dragRef.current = { id: null, ox: 0, oy: 0 };
  };

  const saveLayoutMutation = useMutation({
    mutationFn: async () => {
      const payload = tables.map((t) => ({
        id: t.id,
        pos_x: t.x,
        pos_y: t.y,
        width: TABLE_SIZE,
        height: TABLE_SIZE,
        area: t.area,
      }));
      return api.put("/tables/layout", { tables: payload }, { params: { branch_id: branchId } });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["tables", branchId] });
      queryClient.invalidateQueries({ queryKey: ["table-floor", branchId] });
      toast.success(res.data.message || "Floor plan saved");
    },
    onError: () => toast.error("Failed to save layout"),
  });

  const addTableMutation = useMutation({
    mutationFn: async () => {
      const code = `T-${Date.now().toString().slice(-4)}`;
      return api.post(
        "/tables",
        {
          code,
          name: code,
          area,
          shape: "square",
          pos_x: 60,
          pos_y: 60,
          width: TABLE_SIZE,
          height: TABLE_SIZE,
          status: "Vacant",
        },
        { params: { branch_id: branchId } },
      );
    },
    onSuccess: (res) => {
      const t = res.data.table;
      setTables((prev) => [...prev, t]);
      setSelected(t.id);
      queryClient.invalidateQueries({ queryKey: ["tables", branchId] });
      toast.success(`Added ${t.code} to ${area}`);
    },
    onError: () => toast.error("Failed to add table"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/tables/${id}`, { params: { branch_id: branchId } }),
    onSuccess: (_, id) => {
      setTables((prev) => prev.filter((t) => t.id !== id));
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ["tables", branchId] });
      toast.success("Table removed");
    },
    onError: () => toast.error("Failed to delete table"),
  });

  const updateTable = (id, patch) => {
    pushUndo();
    setTables((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const handleUndo = () => {
    if (!undoStack.length) return toast.info("Nothing to undo");
    const last = undoStack[undoStack.length - 1];
    setTables(JSON.parse(last));
    setUndoStack((prev) => prev.slice(0, -1));
    toast.info("Undone");
  };

  const deleteSelected = () => {
    if (!selected) return;
    deleteMutation.mutate(selected);
  };

  const selectedTable = tables.find((t) => t.id === selected);

  if (isLoading) {
    return (
      <PageContainer>
        <div className="h-40 grid place-items-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="!max-w-none">
      <PageHeader
        title="Table Layout Designer"
        subtitle={`${tables.length} tables — ${user?.default_branch?.name || "branch"}`}
        breadcrumbBadge={<Pill tone="blue">Visual editor</Pill>}
        actions={
          <>
            <Button variant="outline" size="sm" className="h-9" data-testid="designer-undo" onClick={handleUndo}>
              <Undo2 size={14} className="mr-1.5" /> Undo
            </Button>
            <Button
              size="sm"
              className="h-9 bg-[#16A34A] hover:bg-emerald-700"
              data-testid="designer-save"
              onClick={() => saveLayoutMutation.mutate()}
              disabled={saveLayoutMutation.isPending}
            >
              {saveLayoutMutation.isPending ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Save size={14} className="mr-1.5" />
              )}
              Save Layout
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-[260px_1fr_280px] gap-4">
        <Card className="p-3 h-fit">
          <SectionTitle>Areas</SectionTitle>
          <div className="space-y-1 mb-4">
            {areas.map((a) => (
              <button
                key={a}
                data-testid={`designer-area-${a}`}
                onClick={() => setArea(a)}
                className={cn(
                  "w-full text-left px-2.5 h-9 rounded-md text-[12.5px] font-medium border transition-colors",
                  area === a
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-white text-slate-700 border-[#E5E7EB] hover:bg-slate-50",
                )}
              >
                <div className="flex items-center justify-between">
                  <span>{a}</span>
                  <span className="text-[10.5px] text-slate-400">
                    {tables.filter((t) => t.area === a).length}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <SectionTitle>Add Table</SectionTitle>
          <Button
            variant="outline"
            className="w-full h-10"
            data-testid="add-table"
            onClick={() => addTableMutation.mutate()}
            disabled={addTableMutation.isPending}
          >
            <Plus size={14} className="mr-1.5" />
            Add Square Table
          </Button>

          <div className="mt-4 text-[11.5px] text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Tip:</strong> Drag tables on the canvas, then click Save Layout to persist positions.
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <div className="flex items-center gap-2">
              <Layers size={14} className="text-slate-400" />
              <span className="text-[13px] font-semibold text-slate-800">{area}</span>
              <Pill>{visible.length} tables</Pill>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-7 px-2"><Grid3x3 size={14} /></Button>
              <Button variant="ghost" size="sm" className="h-7 px-2"><ZoomOut size={14} /></Button>
              <Button variant="ghost" size="sm" className="h-7 px-2"><ZoomIn size={14} /></Button>
            </div>
          </div>
          <div
            ref={canvasRef}
            data-testid="designer-canvas"
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            className="relative floor-grid"
            style={{ height: 600, userSelect: "none" }}
          >
            <div className="absolute top-2 left-1/2 -translate-x-1/2 text-[10px] tracking-wider uppercase text-slate-400 font-semibold">
              ── Window Side ──
            </div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] tracking-wider uppercase text-slate-400 font-semibold">
              ── Kitchen ──
            </div>

            {visible.map((t) => (
              <div
                key={t.id}
                onMouseDown={(e) => onMouseDown(e, t)}
                data-testid={`designer-table-${t.code}`}
                className={cn(
                  "absolute flex items-center justify-center text-center cursor-move select-none transition-shadow",
                  selected === t.id ? "shadow-lg ring-2 ring-blue-500 ring-offset-2" : "shadow-sm",
                )}
                style={{
                  left: t.x,
                  top: t.y,
                  width: t.w,
                  height: t.h,
                  background: STATUS_FILL[t.status] || "#F1F5F9",
                  border: `2px solid ${STATUS_BORDER[t.status] || "#94A3B8"}`,
                  borderRadius: 8,
                }}
              >
                <div className="leading-tight">
                  <div className="text-[14px] font-bold text-slate-900">{t.name}</div>
                  <div className="text-[10px] text-slate-600 uppercase font-semibold tracking-wide">{t.status}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t bg-slate-50 text-[11.5px] text-slate-500 flex items-center gap-4">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-emerald-200 border border-emerald-500"></span>Vacant</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-red-200 border border-red-500"></span>Occupied</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-amber-200 border border-amber-500"></span>Printed</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-blue-200 border border-blue-500"></span>Multi Order</span>
          </div>
        </Card>

        <Card className="p-4 h-fit">
          <SectionTitle>Table Properties</SectionTitle>
          {selectedTable ? (
            <div className="space-y-3">
              <div>
                <Label className="text-[12px] text-slate-600">Table Code</Label>
                <Input value={selectedTable.code} className="h-9 mt-1 font-mono" readOnly />
              </div>
              <div>
                <Label className="text-[12px] text-slate-600">Display Name</Label>
                <Input value={selectedTable.name} className="h-9 mt-1" data-testid="prop-name" readOnly />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[12px] text-slate-600">X</Label>
                  <Input value={selectedTable.x} className="h-9 mt-1" readOnly />
                </div>
                <div>
                  <Label className="text-[12px] text-slate-600">Y</Label>
                  <Input value={selectedTable.y} className="h-9 mt-1" readOnly />
                </div>
                <div>
                  <Label className="text-[12px] text-slate-600">Width</Label>
                  <Input value={selectedTable.w} className="h-9 mt-1" readOnly />
                </div>
                <div>
                  <Label className="text-[12px] text-slate-600">Height</Label>
                  <Input value={selectedTable.h} className="h-9 mt-1" readOnly />
                </div>
              </div>
              <div>
                <Label className="text-[12px] text-slate-600">Area</Label>
                <Select value={selectedTable.area} onValueChange={(v) => updateTable(selected, { area: v })}>
                  <SelectTrigger className="h-9 mt-1" data-testid="prop-area"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                className="w-full h-9 text-red-600 border-red-200 hover:bg-red-50"
                onClick={deleteSelected}
                data-testid="delete-table"
                disabled={deleteMutation.isPending}
              >
                <Trash2 size={14} className="mr-1.5" /> Delete Table
              </Button>
            </div>
          ) : (
            <div className="text-[12px] text-slate-400 text-center py-8 border border-dashed rounded-lg">
              Click a table on the canvas to edit its properties
            </div>
          )}
        </Card>
      </div>
    </PageContainer>
  );
}
